import React, { useState, useEffect } from "react";
import { Alert, Modal, Button, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import RIcon from "../reservas/components/ReservasIcons";
import MarketingOptIn from "../reservas/components/MarketingOptIn";
import { SucursalBanner, SucursalCurrent, SucursalPicker, SucursalesSection } from "../reservas/components/Sucursales";
import { backTarget } from "../reservas/components/sucursalesModel";
import {
  SingleDatePickerPill,
  FALLBACK_IMAGE,
  WEEK_DAYS_ES,
  HoldCountdown,
  QtySelect,
  resolveUrl,
} from "../reservas/components/ReservaShared";
import {
  SPORT_LABELS,
  SURFACE_LABELS,
  ENCLOSURE_LABELS,
  SERVICE_LABELS,
  DURATION_LABELS,
} from "../../constants/courtDomain";
import { useCourtReservationFlow } from "./useCourtReservationFlow";
import CourtSlotSelector from "./components/CourtSlotSelector";
import WaitlistJoin from "../listaDeEspera/WaitlistJoin";
import { isWaitlistEnabled } from "../listaDeEspera/waitlistModel";
import ClubOpenMatchesButton from "./components/ClubOpenMatchesButton";
import OpenMatchRulesStep from "./components/OpenMatchRulesStep";
import OpenMatchPublished from "./components/OpenMatchPublished";
import OpenMatchJoinForm from "./components/OpenMatchJoinForm";
import { wantsOpenMatchCreate } from "./openMatchesModel";
import PlayersCountField from "./components/PlayersCountField";
import BookingStepper from "./components/BookingStepper";
import OpenMatchSummary from "./components/OpenMatchSummary";
import "./components/OpenMatchCompact.css";
import BuyerDataFields from "./components/BuyerDataFields";
import PerPlayerPrice from "./components/PerPlayerPrice";
import { buildMapQuery } from "./components/mapConfig";
import ReviewsSection from "../reservas/components/ReviewsSection";
import ClubBirthdaysSection from "./components/ClubBirthdaysSection";
import { visibleServices } from "../cumpleanos/birthdayModel";
import { resolveSlotDeposit, slotRequiresOnlinePayment } from "./courtDeposit";

const getSportIcon = () => {
  return "ball";
};

const getServiceIcon = (service) => {
  switch (service) {
    case "wifi":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5a13.3 13.3 0 0 1 14 0" />
          <path d="M8.5 16a7.7 7.7 0 0 1 7 0" />
          <path d="M2 9a18.5 18.5 0 0 1 20 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" strokeWidth="3" />
        </svg>
      );
    case "vestuario":
      return <RIcon name="user" width={18} height={18} />;
    case "gimnasio":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6.5 6.5h11M6.5 17.5h11M18 5v14M6 5v14M3 8v8M21 8v8" />
        </svg>
      );
    case "estacionamiento":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
        </svg>
      );
    case "parrillas":
      return <RIcon name="spark" width={18} height={18} />;
    case "ayuda_medica":
      return <RIcon name="shield" width={18} height={18} />;
    case "bar":
    case "bar_restaurante":
      return <RIcon name="glass" width={18} height={18} />;
    case "torneos":
      return <RIcon name="star" width={18} height={18} />;
    case "cumpleanos":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 12 20 22 4 22 4 12" />
          <rect x="2" y="7" width="20" height="5" />
          <line x1="12" y1="22" x2="12" y2="7" />
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
      );
    case "escuelita_deportiva":
      return <RIcon name="users" width={18} height={18} />;
    case "colegios":
      return <RIcon name="building" width={18} height={18} />;
    case "quincho":
      return <RIcon name="tent" width={18} height={18} />;
    case "disposicion_de_paletas":
      return <RIcon name="court" width={18} height={18} />;
    default:
      return <RIcon name="info" width={18} height={18} />;
  }
};

const STEP_CLUB = "club";
const STEP_SLOTS = "slots";
const STEP_FORM = "form";
// Solo en modo "partido abierto" (?crear=partido): Elegir turno > Partido abierto > Tus datos > Publicar.
const STEP_MATCH = "match";
const OPEN_MATCH_STEPS = ["Elegir turno", "Partido abierto", "Tus datos", "Publicar"];

/**
 * Desktop club detail wizard (≥769px). Renders a premium 3-step workflow
 * mirroring BarDetailDesktop:
 * 1. STEP_CLUB: Club info sections ordered as requested, and sidebar filters.
 * 2. STEP_SLOTS: Wide ATC Sports style grid availability.
 * 3. STEP_FORM: Spacious checkout form.
 */
export default function ClubDetailDesktop({ club, courts, sucursales = [], casaCentral = null, heroClub = null, sedes = null }) {
  // On a casa central the hero stays the casa's while `club` is the chosen sede.
  const hero = heroClub || club;
  const navigate = useNavigate();
  const services = visibleServices(club);
  const bookingsEnabled = club.onlineBookingEnabled === true;
  const location = useLocation();
  const openMatchMode = wantsOpenMatchCreate(location?.search) && club.openMatches?.enabled === true;
  const flow = useCourtReservationFlow({ club, mode: openMatchMode ? "openMatch" : "reservation" });

  const [activeTab, setActiveTab] = useState(sucursales.length > 0 ? "sucursales" : "desc");
  const [step, setStep] = useState(() => {
    if (!bookingsEnabled) return STEP_CLUB;
    if (flow.restoredStep === "hold" || flow.restoredStep === "matchData") return STEP_FORM;
    if (flow.restoredStep === "slots") return STEP_SLOTS;
    return STEP_CLUB;
  });

  const {
    durations,
    sports,
    selectedDate,
    setSelectedDate,
    selectedDuration,
    setSelectedDuration,
    selectedSport,
    setSelectedSport,
    minDate,
    maxDate,
    hasRequestedAvailability,
    requestAvailability,
    courtsForSelectedDuration,
    loadingAvail,
    availError,
    selectedCourtId,
    selectedStart,
    selectSlot,
    playersCount,
    setPlayersCount,
    playersEdited,
    setPlayersEdited,
    defaultPlayers,
    maxPlayers,
    holdId,
    holdExpiresAt,
    holdLoading,
    holdError,
    handleAdvanceToForm,
    handleHoldExpired,
    register,
    handleSubmit,
    errors,
    trigger,
    submitting,
    submitError,
    consent,
    setConsent,
    marketingOptIn,
    setMarketingOptIn,
    onSubmit,
    selectedCountry,
    setSelectedCountry,
    showPaymentModal,
    setShowPaymentModal,
    modalCard,
    setModalCard,
    cardOptions,
    selectedCardOption,
    setPendingValues,
    handleConfirmPaymentSelection,
  } = flow;

  // Track active tab using IntersectionObserver
  useEffect(() => {
    if (step !== STEP_CLUB || !club) return;
    if (typeof window === "undefined" || !window.IntersectionObserver) return;
    const sections = [
      "sec-desc",
      "sec-sucursales",
      "sec-ubi",
      "sec-deportes",
      "sec-canchas",
      "sec-horarios",
      "sec-contacto",
      "sec-servicios",
      "sec-fotos",
      "sec-resenas",
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            if (id === "sec-desc") setActiveTab("desc");
            else if (id === "sec-sucursales") setActiveTab("sucursales");
            else if (id === "sec-ubi") setActiveTab("ubi");
            else if (id === "sec-deportes") setActiveTab("deportes");
            else if (id === "sec-canchas") setActiveTab("instalaciones");
            else if (id === "sec-horarios") setActiveTab("horarios");
            else if (id === "sec-contacto") setActiveTab("contacto");
            else if (id === "sec-servicios") setActiveTab("servicios");
            else if (id === "sec-fotos") setActiveTab("fotos");
            else if (id === "sec-resenas") setActiveTab("resenas");
          }
        });
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0,
      }
    );

    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [step, club]);

  // Sync step from restored session
  useEffect(() => {
    if (!bookingsEnabled) {
      setStep(STEP_CLUB);
      return;
    }

    if (flow.restoredStep === "hold" || flow.restoredStep === "matchData") {
      setStep(STEP_FORM);
    } else if (flow.restoredStep === "slots") {
      setStep(STEP_SLOTS);
    }
  }, [flow.restoredStep, bookingsEnabled]);

  const img = club.image || FALLBACK_IMAGE;
  const heroImg = hero.image || FALLBACK_IMAGE;
  const openingHours = Array.isArray(club.openingHours) ? club.openingHours : [];
  const todayIndex = dayjs().day();

  const goToSlots = () => {
    setStep(STEP_SLOTS);
    requestAvailability();
    if (typeof window !== "undefined" && typeof window.scrollTo === "function" && process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  };

  const handleHoldExpiredLocal = () => {
    handleHoldExpired();
    setStep(STEP_SLOTS);
  };

  const handleAdvanceToFormLocal = async () => {
    const result = await handleAdvanceToForm();
    if (result?.reused || result?.created) {
      setStep(openMatchMode ? STEP_MATCH : STEP_FORM);
    }
  };

  // Entrando por "Crear partido abierto" se arranca directo en Elegir turno, una sola vez.
  const openMatchStartedRef = React.useRef(false);
  useEffect(() => {
    if (!openMatchMode || !bookingsEnabled || openMatchStartedRef.current) return;
    openMatchStartedRef.current = true;
    setStep(STEP_SLOTS);
    flow.requestAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMatchMode, bookingsEnabled]);

  const onSubmitLocal = async (values) => {
    const result = await onSubmit(values);
    if (result === "slots") setStep(STEP_SLOTS);
  };

  const selectedCourt = courts.find(
    (c) => c._id === selectedCourtId || c.courtId === selectedCourtId
  );
  const displayPrice = selectedStart?.price ? `$${Number(selectedStart.price).toLocaleString("es-AR")}` : null;

  // ================================================================
  // OPEN MATCH (T10): published / Partido abierto / Tus datos
  // ================================================================
  // Court/summary data shared by the open-match steps (the `courts` prop can be empty: the availability knows the court too).
  const matchCourt =
    selectedCourt ||
    (Array.isArray(flow.availability?.courts) ? flow.availability.courts : []).find(
      (c) => c._id === selectedCourtId || c.courtId === selectedCourtId,
    );
  const matchSummary = (
    <OpenMatchSummary
      variant="desktop"
      clubName={club.name}
      image={img}
      courtName={matchCourt?.name}
      date={selectedDate}
      slotStart={selectedStart?.slotStart}
      duration={selectedDuration}
      alreadyPlayers={flow.matchRules.alreadyPlayers}
      capacity={flow.matchCapacity}
      price={selectedStart?.price}
    />
  );

  if (flow.published) {
    return (
      <div className="reservas-root club-detail-desktop">
        <div className="wrap reservar oma-shell">
          <BookingStepper steps={OPEN_MATCH_STEPS} current={4} allDone compact />
          <OpenMatchPublished info={flow.published} />
        </div>
      </div>
    );
  }

  if (openMatchMode && bookingsEnabled && (step === STEP_MATCH || step === STEP_FORM)) {
    const inMatch = step === STEP_MATCH;
    // Creating needs a session: a guest is sent to /login here and comes back to the data step with the rules.
    const continueFromRules = async () => {
      const result = await flow.continueOpenMatch();
      if (result?.ok) setStep(STEP_FORM);
    };
    return (
      <div className="reservas-root club-detail-desktop">
        <div className="wrap reservar oma-shell">
          <button className="crumb" onClick={() => setStep(inMatch ? STEP_SLOTS : STEP_MATCH)}>
            <RIcon name="chevL" width={16} height={16} /> {inMatch ? "Volver a la selección" : "Volver al partido"}
          </button>
          <BookingStepper steps={OPEN_MATCH_STEPS} current={inMatch ? 2 : 3} compact />
          <div className="reservar-grid">
            <div className="form-card">
              {inMatch ? (
                <>
                  <h3>Armá tu partido</h3>
                  <OpenMatchRulesStep
                    compact
                    capacity={flow.matchCapacity}
                    rules={flow.matchRules}
                    closeHours={club.openMatches?.closeHours}
                    date={flow.dateLabel}
                    slotStart={selectedStart?.slotStart}
                    price={selectedStart?.price}
                    onChange={flow.setMatchRules}
                  />
                  <div className="oma-actions">
                    <button type="button" className="r-btn-primary" onClick={continueFromRules}>
                      Continuar <RIcon name="arrowR" width={17} height={17} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3>Completá tus datos</h3>
                  <OpenMatchJoinForm
                    variant="desktop"
                    compact
                    title={null}
                    clubName={club.name}
                    defaults={flow.authUser}
                    submitting={flow.submitting}
                    error={flow.createError}
                    onCancel={() => setStep(STEP_MATCH)}
                    onSubmit={async (values, country, optIn) => {
                      const result = await flow.publishOpenMatch(values, country, optIn);
                      if (result === "slots") setStep(STEP_SLOTS);
                    }}
                    submitLabel="Publicar partido"
                    submittingLabel="Publicando…"
                    consentRules="cada jugador paga su parte en el club y el partido se cierra a la hora indicada."
                  />
                </>
              )}
            </div>
            {matchSummary}
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: FORM (Check-out)
  // ================================================================
  if (bookingsEnabled && step === STEP_FORM) {
    return (
      <div className="reservas-root club-detail-desktop">
        <div className="wrap reservar">
          <button className="crumb" onClick={() => setStep(STEP_SLOTS)}>
            <RIcon name="chevL" width={16} height={16} /> Volver a la selección
          </button>

          <BookingStepper
            steps={["Elegir turno", "Tus datos", slotRequiresOnlinePayment(selectedStart, club) ? "Pago" : "Confirmar"]}
            current={2}
          />

          {holdExpiresAt && (
            <HoldCountdown expiresAt={holdExpiresAt} onExpired={handleHoldExpiredLocal} />
          )}

          <div className="reservar-grid">
            <div className="form-card">
              <h3>Completá tus datos</h3>
              <p className="fc-sub">Usamos estos datos para confirmar tu reserva y enviarte el comprobante.</p>

              <form onSubmit={handleSubmit(onSubmitLocal)}>
                <div className="fgrid">
                  <BuyerDataFields
                    register={register}
                    errors={errors}
                    trigger={trigger}
                    country={selectedCountry}
                    onCountryChange={setSelectedCountry}
                    showNotes
                    emailHint="Te enviamos el comprobante acá."
                  />
                </div>

                <div className="reservation-details-box">
                  <div className="rdb-title">Detalles del turno</div>
                  {selectedCourt && <div className="rdb-row"><span><RIcon name="ticket" width={14} height={14} /> Cancha</span><strong>{selectedCourt.name}</strong></div>}
                  <div className="rdb-row"><span><RIcon name="cal" width={14} height={14} /> Fecha</span><strong>{selectedDate.format("DD/MM/YYYY")}</strong></div>
                  {selectedStart && <div className="rdb-row"><span><RIcon name="clock" width={14} height={14} /> Horario</span><strong>{selectedStart.slotStart} hs a {selectedStart.slotEnd} hs</strong></div>}
                  <div className="rdb-row"><span><RIcon name="clock" width={14} height={14} /> Duración</span><strong>{DURATION_LABELS[selectedDuration] || `${selectedDuration} min`}</strong></div>
                  {selectedCourt && (
                    <div className="rdb-row">
                      <span><RIcon name="pin" width={14} height={14} /> Características</span>
                      <strong style={{ textAlign: "right" }}>
                        {[
                          SPORT_LABELS[selectedCourt.sport] || selectedCourt.sport,
                          SURFACE_LABELS[selectedCourt.surface] || selectedCourt.surface,
                          ENCLOSURE_LABELS[selectedCourt.enclosure] || selectedCourt.enclosure,
                          selectedCourt.lighting ? "Con iluminación" : "Sin iluminación",
                          selectedCourt.courtCategory,
                        ].filter(Boolean).join(" · ")}
                      </strong>
                    </div>
                  )}
                  <div className="rdb-row" style={{ alignItems: "flex-start" }}>
                    <span><RIcon name="users" width={14} height={14} /> Jugadores</span>
                    <div style={{ maxWidth: 320 }}>
                      <PlayersCountField
                        value={playersCount}
                        onChange={setPlayersCount}
                        edited={playersEdited}
                        onEditedChange={setPlayersEdited}
                        defaultPlayers={defaultPlayers}
                        max={maxPlayers}
                        sportLabel={selectedCourt ? (SPORT_LABELS[selectedCourt.sport] || selectedCourt.sport) : ""}
                      />
                    </div>
                  </div>
                  <PerPlayerPrice
                    totalPrice={selectedStart?.price}
                    players={playersCount}
                    variant="detail"
                  />
                </div>

                {!slotRequiresOnlinePayment(selectedStart, club) ? (
                  <div style={{ padding: "12px 16px", background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: "8px", fontSize: "13px", color: "#4b5563", marginTop: "16px", marginBottom: "8px", lineHeight: "1.5" }}>
                    <strong>Aclaración:</strong> No pagás nada online. El precio de la reserva lo abonarás presencialmente al momento del turno o mediante el método de pago elegido por el club (deberás estar atento ante una posible comunicación de parte de ellos).
                  </div>
                ) : (
                  <div style={{
                    padding: "16px 20px",
                    background: "#fffaf0",
                    border: "1px solid #f97316",
                    borderRadius: "8px",
                    fontSize: "13.5px",
                    color: "#7c2d12",
                    marginTop: "16px",
                    marginBottom: "8px",
                    lineHeight: "1.5",
                    display: "flex",
                    gap: "12px",
                    alignItems: "flex-start"
                  }}>
                    <div style={{ color: "#ea580c", display: "flex", alignItems: "center", marginTop: "2px" }}>
                      <RIcon name="info" width={18} height={18} />
                    </div>
                    <div>
                      <strong style={{ color: "#9a3412", display: "block", marginBottom: "4px" }}>
                        Sólo se debitará el importe del anticipo/adelanto
                      </strong>
                      <span>
                        Abonarás luego el saldo restante de la reserva (si lo hubiera).
                        {club.cancellationAllowed !== false && club.cancellationAllowed !== "false" ? (
                          ` En caso de que canceles con ${club.cancellationPolicyHours || 24} hs de anticipación, se te reintegrará el importe automáticamente.`
                        ) : (
                          " Las reservas no son cancelables."
                        )}
                      </span>
                    </div>
                  </div>
                )}

                <MarketingOptIn venueName={club.name} checked={marketingOptIn} onChange={setMarketingOptIn} />

                <div className="consent" onClick={() => setConsent((c) => !c)}>
                  <span className={`cbox${consent ? " on" : ""}`}>
                    {consent && <RIcon name="check" width={13} height={13} />}
                  </span>
                  <p>
                    Acepto la <strong>política de reservas</strong> de {club.name}
                    {(club.cancellationAllowed !== false && club.cancellationAllowed !== "false")
                      ? `: cancelación sin cargo hasta ${club.cancellationPolicyHours || 24} hs antes.`
                      : ": las reservas no son cancelables."}
                  </p>
                </div>

                {submitError && <Alert variant="danger" className="mt-3" style={{ fontSize: 13 }}>{submitError}</Alert>}

                <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                  <button type="button" className="r-btn-soft" style={{ padding: "13px 22px" }} onClick={() => setStep(STEP_SLOTS)}>Volver</button>
                  <button
                    type="submit"
                    className="r-btn-primary"
                    style={{ flex: 1, padding: 14, justifyContent: "center", opacity: consent ? 1 : 0.5 }}
                    disabled={submitting || !consent}
                  >
                    {submitting
                      ? <Spinner animation="border" size="sm" />
                      : <>{slotRequiresOnlinePayment(selectedStart, club) ? "Ir al pago" : "Confirmar reserva"}<RIcon name="arrowR" width={17} height={17} /></>}
                  </button>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>
                  <RIcon name="shield" width={15} height={15} style={{ color: "#b1c20e" }} />
                  {slotRequiresOnlinePayment(selectedStart, club)
                    ? "Te vamos a redirigir a nuestro sistema de pago seguro para confirmar la seña."
                    : "Al confirmar, la reserva se procesará automáticamente. No habrá necesidad de cargar un método de pago."}
                </div>
              </form>
            </div>

            <div className="sum-card">
              <div className="sum-img">
                <img src={img} alt={club.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
                <div className="sc" />
                <div className="bn">{club.name}</div>
              </div>
              <div className="sum-body">
                <h4>Resumen</h4>
                {selectedCourt && <div className="rs-row"><span className="k"><RIcon name="ticket" />Cancha</span><span className="v">{selectedCourt.name}</span></div>}
                <div className="rs-row"><span className="k"><RIcon name="cal" />Fecha</span><span className="v">{selectedDate.format("DD/MM/YYYY")}</span></div>
                {selectedStart && <div className="rs-row"><span className="k"><RIcon name="clock" />Horario</span><span className="v">{selectedStart.slotStart} hs</span></div>}
                <div className="rs-row"><span className="k"><RIcon name="clock" />Duración</span><span className="v">{selectedDuration} min</span></div>
                {selectedStart?.price && (
                  <>
                    <div className="rs-div" />
                    <div className="rs-row" style={{ fontSize: "14px", color: "#4b5563" }}>
                      <span className="k">Precio de la cancha</span>
                      <span className="v" style={{ fontWeight: 700 }}>{displayPrice}</span>
                    </div>
                    <PerPlayerPrice totalPrice={selectedStart.price} players={playersCount} />
                    {(() => {
                      const basePrice = Number(selectedStart.price);
                      const fakeFee = Math.round(basePrice * 0.01);
                      const nowPaying = resolveSlotDeposit(selectedStart, club);
                      return (
                        <>
                          <div className="rs-row" style={{ fontSize: "13.5px", background: "var(--zoco-lime-tint, #F6F9E8)", padding: "6px 8px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 0" }}>
                            <span className="k" style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 600 }}>
                              <RIcon name="ticket" width={14} height={14} /> Tasa de servicio
                            </span>
                            <span className="v" style={{ display: "flex", gap: "8px" }}>
                              <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>${fakeFee.toLocaleString("es-AR")}</span>
                              <strong style={{ color: "var(--zoco-lime-strong, #8FA80A)" }}>$0</strong>
                            </span>
                          </div>

                          <div className="rs-note" style={{ justifyContent: "center", marginTop: "8px", marginBottom: "8px", width: "100%", display: "flex", alignItems: "center", gap: "6px" }}>
                            <RIcon name="shield" width={15} height={15} />
                            Reserva segura, sin costos extra de servicio.
                          </div>
                          
                          <div className="rs-div" />
                          <div className="rs-total" style={{ padding: "4px 0" }}>
                            <span className="k">Ahora pagás</span>
                            <span className="v" style={{ color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 800 }}>
                              ${nowPaying.toLocaleString("es-AR")}
                            </span>
                          </div>
                          <div style={{ fontSize: "12px", fontStyle: "italic", color: "#6b7280", textAlign: "right", marginTop: "-4px", marginBottom: "8px" }}>
                            {slotRequiresOnlinePayment(selectedStart, club) ? "Adelanto para reservar la cancha" : "Abonás el total en el club"}
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal show={showPaymentModal} onHide={() => { setShowPaymentModal(false); setPendingValues(null); }} centered backdrop="static" className="payment-modal">
          <Modal.Header closeButton><Modal.Title>Elegí cómo pagar</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Tarjeta</label>
              <QtySelect
                className="payment-select"
                placeholder="Elegí una tarjeta"
                value={modalCard}
                onChange={(val) => setModalCard(val == null ? "" : String(val))}
                options={(cardOptions || []).map((opt) => ({ value: opt.id, label: opt.label, disabled: opt.disabled }))}
              />
            </div>
            {selectedCardOption && (
              <div className="mb-3">
                <label className="form-label">Cuotas</label>
                <QtySelect className="payment-select" placeholder="Elegí la cantidad de cuotas" value="1" disabled={true} onChange={() => {}} options={[{ value: "1", label: "1 cuota (sin interés)" }]} />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowPaymentModal(false); setPendingValues(null); }}>Cancelar</Button>
            <Button variant="primary" onClick={handleConfirmPaymentSelection} disabled={!selectedCardOption}>Continuar</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  // ================================================================
  // STEP: SLOTS (ATC Sports Grid View)
  // ================================================================
  if (bookingsEnabled && step === STEP_SLOTS) {
    return (
      <div className="reservas-root club-detail-desktop">
        <div className="wrap bar-detail">
          <button className="crumb" onClick={() => setStep(STEP_CLUB)}>
            <RIcon name="chevL" width={16} height={16} /> Volver al club
          </button>

          <div className="steps" style={{ marginBottom: 24 }}>
            <div className="step done"><span className="dot"><RIcon name="check" width={13} height={13} /></span>Info del club</div>
            <div className="step-sep done" />
            <div className="step on"><span className="dot">2</span>Elegir turno</div>
            <div className="step-sep" />
            <div className="step"><span className="dot">3</span>Tus datos</div>
          </div>

          <div className="bd-grid">
            <div style={{ minWidth: 0 }}>
              <div className="bd-section">
                <h3 className="section-title">Elegí tu turno</h3>
                <CourtSlotSelector
                  courts={flow.availability.courts}
                  sports={sports}
                  selectedSport={selectedSport}
                  setSelectedSport={setSelectedSport}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  selectedDuration={selectedDuration}
                  setSelectedDuration={setSelectedDuration}
                  selectedCourtId={selectedCourtId}
                  selectedStart={selectedStart}
                  onSelectSlot={selectSlot}
                  onContinue={handleAdvanceToFormLocal}
                  loading={loadingAvail}
                  error={availError}
                  minDate={minDate}
                  maxDate={maxDate}
                  reservationWindowDays={club.reservationWindowDays}
                  isMobile={false}
                />
                <WaitlistJoin
                  enabled={isWaitlistEnabled(club)}
                  barId={club._id}
                  kind="court"
                  date={selectedDate?.format("YYYY-MM-DD")}
                  criteria={{ ...(selectedSport ? { sport: selectedSport } : {}), durationMinutes: Number(selectedDuration) }}
                  summary={[selectedSport, selectedDuration ? selectedDuration + " min" : ""].filter(Boolean).join(" · ")}
                />
              </div>

              {holdError && <Alert variant="danger" className="mt-3">{holdError}</Alert>}
              {flow.createError && <Alert variant="danger" className="mt-3">{flow.createError}</Alert>}
            </div>

            <div className="bd-sidebar-sticky">
              <div className="reserve-stick">
                <div className="rs-head"><div className="t">Tu turno</div><div className="n">{club.name}</div></div>
                <div className="rs-body">
                  {!selectedStart ? (
                    <div className="rs-empty">
                      <RIcon name="court" width={34} height={34} />
                      <br />
                      Hacé clic en una celda de la grilla para elegir tu horario y duración.
                    </div>
                  ) : (
                    <>
                      {selectedCourt && <div className="rs-row"><span className="k"><RIcon name="ticket" />Cancha</span><span className="v">{selectedCourt.name}</span></div>}
                      <div className="rs-row"><span className="k"><RIcon name="cal" />Fecha</span><span className="v">{selectedDate.format("DD/MM/YYYY")}</span></div>
                      <div className="rs-row"><span className="k"><RIcon name="clock" />Horario</span><span className="v">{selectedStart.slotStart} hs</span></div>
                      <div className="rs-row"><span className="k"><RIcon name="clock" />Duración</span><span className="v">{selectedDuration} min</span></div>
                      {/* Editable here as well as in the checkout form: this panel
                          already shows the "cada uno paga" split, and a number the
                          buyer can read but not change is a dead end. Both controls
                          drive the same `playersCount` state, so they cannot drift. */}
                      <div className="rs-div" />
                      <PlayersCountField
                        value={playersCount}
                        onChange={setPlayersCount}
                        edited={playersEdited}
                        onEditedChange={setPlayersEdited}
                        defaultPlayers={defaultPlayers}
                        max={maxPlayers}
                        sportLabel={selectedCourt ? (SPORT_LABELS[selectedCourt.sport] || selectedCourt.sport) : ""}
                      />
                      {selectedStart?.price && (
                        <>
                          <div className="rs-div" />
                          <div className="rs-row" style={{ fontSize: "14px", color: "#4b5563" }}>
                            <span className="k">Precio de la cancha</span>
                            <span className="v" style={{ fontWeight: 700 }}>{displayPrice}</span>
                          </div>
                          <PerPlayerPrice totalPrice={selectedStart.price} players={playersCount} />
                          {(() => {
                            const basePrice = Number(selectedStart.price);
                            const fakeFee = Math.round(basePrice * 0.01);
                            const nowPaying = resolveSlotDeposit(selectedStart, club);
                            return (
                              <>
                                <div className="rs-row" style={{ fontSize: "13.5px", background: "var(--zoco-lime-tint, #F6F9E8)", padding: "6px 8px", borderRadius: "6px", display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 0" }}>
                                  <span className="k" style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 600 }}>
                                    <RIcon name="ticket" width={14} height={14} /> Tasa de servicio
                                  </span>
                                  <span className="v" style={{ display: "flex", gap: "8px" }}>
                                    <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>${fakeFee.toLocaleString("es-AR")}</span>
                                    <strong style={{ color: "var(--zoco-lime-strong, #8FA80A)" }}>$0</strong>
                                  </span>
                                </div>

                                <div className="rs-note" style={{ justifyContent: "center", marginTop: "8px", marginBottom: "8px", width: "100%", display: "flex", alignItems: "center", gap: "6px" }}>
                                  <RIcon name="shield" width={15} height={15} />
                                  Reserva segura, sin costos extra de servicio.
                                </div>
                                
                                <div className="rs-div" />
                                <div className="rs-total" style={{ padding: "4px 0" }}>
                                  <span className="k">Ahora pagás</span>
                                  <span className="v" style={{ color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 800 }}>
                                    ${nowPaying.toLocaleString("es-AR")}
                                  </span>
                                </div>
                                <div style={{ fontSize: "12px", fontStyle: "italic", color: "#6b7280", textAlign: "right", marginTop: "-4px", marginBottom: "8px" }}>
                                  {slotRequiresOnlinePayment(selectedStart, club) ? "Adelanto para reservar la cancha" : "Abonás el total en el club"}
                                </div>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                  <button
                    className="r-btn-primary rs-cta"
                    disabled={!selectedStart || holdLoading}
                    onClick={handleAdvanceToFormLocal}
                  >
                    {holdLoading ? <Spinner animation="border" size="sm" /> : <>Continuar <RIcon name="arrowR" width={17} height={17} /></>}
                  </button>
                </div>
              </div>

              <div className="policy-box">
                {(club.cancellationAllowed === false || club.cancellationAllowed === "false") ? (
                  <>
                    <div className="policy"><RIcon name="cal" width={16} height={16} />Las reservas se pueden realizar hasta con {club.reservationWindowDays || 6} días de antelación.</div>
                    <div className="policy"><RIcon name="alert" width={16} height={16} style={{ color: "var(--zoco-danger)" }} />Las reservas no son cancelables.</div>
                  </>
                ) : (
                  <>
                    <div className="policy"><RIcon name="cal" width={16} height={16} />Las reservas se pueden realizar hasta con {club.reservationWindowDays || 6} días de antelación.</div>
                    <div className="policy"><RIcon name="shield" width={16} height={16} />Cancelación sin cargo hasta {club.cancellationPolicyHours || 24} hs antes.</div>
                  </>
                )}
                {club.additionalNotes && <div className="policy"><RIcon name="info" width={16} height={16} />{club.additionalNotes}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: CLUB DETAIL (Acerca de, Ubicacion, Deportes, Canchas, Horarios, Servicios, Galeria)
  // ================================================================
  return (
    <div className="reservas-root club-detail-desktop">
      <div className="wrap bar-detail">
        <button className="crumb" onClick={() => navigate(backTarget("canchas", casaCentral).path)}>
          <RIcon name="chevL" width={16} height={16} /> {backTarget("canchas", casaCentral).label}
        </button>

        <SucursalBanner kind="canchas" casaCentral={casaCentral} />

        <div className="bd-hero">
          <img src={heroImg} alt={hero.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
          <div className="bd-hero-scrim" />
          <div className="bd-hero-info">
            <h1>{hero.name}</h1>
            <div className="meta">
              {hero.address && <span><RIcon name="pin" width={16} height={16} /> {hero.address}</span>}
            </div>
          </div>
        </div>

        <div className="bd-grid bd-info-grid">
          <div>
            <div className="info-tabs-sticky">
              {sucursales.length > 0 && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "sucursales" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("sucursales");
                    document.getElementById("sec-sucursales")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Sucursales
                </button>
              )}
              <button
                type="button"
                className={`info-tab-link ${activeTab === "desc" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("desc");
                  document.getElementById("sec-desc")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Descripción
              </button>
              <button
                type="button"
                className={`info-tab-link ${activeTab === "ubi" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("ubi");
                  document.getElementById("sec-ubi")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Ubicación
              </button>
              {sports.length > 0 && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "deportes" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("deportes");
                    document.getElementById("sec-deportes")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Deportes
                </button>
              )}
              <button
                type="button"
                className={`info-tab-link ${activeTab === "instalaciones" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("instalaciones");
                  document.getElementById("sec-canchas")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Canchas
              </button>
              {openingHours.length > 0 && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "horarios" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("horarios");
                    document.getElementById("sec-horarios")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Horarios
                </button>
              )}
              {(club.contactPhone || club.contactEmail || club.website || club.instagram) && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "contacto" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("contacto");
                    document.getElementById("sec-contacto")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Contacto
                </button>
              )}
              {services.length > 0 && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "servicios" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("servicios");
                    document.getElementById("sec-servicios")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Servicios
                </button>
              )}
              {Array.isArray(club.galleryimage) && club.galleryimage.length > 0 && (
                <button
                  type="button"
                  className={`info-tab-link ${activeTab === "fotos" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("fotos");
                    document.getElementById("sec-fotos")?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                >
                  Galería
                </button>
              )}
              <button
                type="button"
                className={`info-tab-link ${activeTab === "resenas" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("resenas");
                  document.getElementById("sec-resenas")?.scrollIntoView({ behavior: "smooth", block: "center" });
                }}
              >
                Reseñas
              </button>
            </div>

            <SucursalesSection kind="canchas" sucursales={sucursales} id="sec-sucursales" isDesktop sedes={sedes} />

            <div id="sec-desc" className="bd-section-stacked">
              <h3 className="section-title">Descripción</h3>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: "#4b5563" }}>
                {club.description || club.shortDescription || "Club disponible para reservas de turnos deportivos."}
              </p>
            </div>

            {bookingsEnabled && club.birthdays && (
              <ClubBirthdaysSection
                id="sec-cumpleanos"
                className="bd-section-stacked"
                titleClassName="section-title"
                birthdays={club.birthdays}
                onReserve={() => navigate(`/canchas/${club._id}/cumpleanos`)}
              />
            )}

            <div id="sec-ubi" className="bd-section-stacked">
              <h3 className="section-title">Ubicación</h3>
              {club.address && (
                <p className="bd-address-text" style={{ fontSize: "14px", color: "#4b5563", display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                  <RIcon name="pin" width={16} height={16} /> {club.address}
                </p>
              )}
              <div style={{ height: "300px", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", background: "#f3f4f6" }}>
                {(() => {
                  const mapQuery = buildMapQuery(club);
                  if (!mapQuery) return null;
                  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
                  return <iframe src={mapSrc} width="100%" height="100%" style={{ border: 0, display: "block" }} allowFullScreen="" loading="lazy" title="Google Maps" />;
                })()}
              </div>
            </div>

            {sports.length > 0 && (
              <div id="sec-deportes" className="bd-section-stacked">
                <h3 className="section-title">Deportes</h3>
                <div className="details-grid-premium">
                  {sports.map((s) => (
                    <div className="detail-item-premium" key={s}>
                      <span className="icon">
                        <RIcon name={getSportIcon(s)} width={18} height={18} />
                      </span>
                      <div className="info">
                        <span className="label">Deporte disponible</span>
                        <strong className="val">{SPORT_LABELS[s] || s}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div id="sec-canchas" className="bd-section-stacked">
              <h3 className="section-title">Canchas</h3>
              {courts.length === 0 ? (
                <p style={{ fontSize: 14, color: "#6b7280" }}>Este club aún no cargó sus canchas.</p>
              ) : (
                <div className="details-grid-premium">
                  {courts.map((court) => (
                    <div className="detail-item-premium" key={court._id || court.name}>
                      <span className="icon"><RIcon name="ticket" width={18} height={18} /></span>
                      <div className="info">
                        <span className="label">{court.name}</span>
                        <strong className="val" style={{ whiteSpace: "normal", fontWeight: "450" }}>
                          {[
                            SPORT_LABELS[court.sport] || court.sport,
                            SURFACE_LABELS[court.surface] || court.surface,
                            ENCLOSURE_LABELS[court.enclosure] || court.enclosure,
                            court.lighting ? "Con iluminación" : "Sin iluminación",
                            court.courtCategory,
                          ].filter(Boolean).join(" · ")}
                        </strong>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {openingHours.length > 0 && (
              <div id="sec-horarios" className="bd-section-stacked">
                <h3 className="section-title">Horarios de atención</h3>
                <div className="hours-list-premium">
                  {openingHours.slice().sort((a, b) => a.weekday - b.weekday).map((h, i) => (
                    <div className={`hours-row-premium${h.weekday === todayIndex ? " today" : ""}${!h.enabled ? " closed" : ""}`} key={i}>
                      <span className="d">{WEEK_DAYS_ES[h.weekday]}</span>
                      <span className="h">{h.enabled ? `${h.open} – ${h.close}` : "Cerrado"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(club.contactPhone || club.contactEmail || club.website || club.instagram) && (
              <div id="sec-contacto" className="bd-section-stacked">
                <h3 className="section-title">Contacto</h3>
                <div className="details-grid-premium">
                  {club.contactPhone && (
                    <div className="detail-item-premium">
                      <span className="icon"><RIcon name="phone" width={18} height={18} /></span>
                      <div className="info">
                        <span className="label">Celular / Teléfono</span>
                        <strong className="val">{club.contactPhone}</strong>
                      </div>
                    </div>
                  )}
                  {club.contactEmail && (
                    <div className="detail-item-premium">
                      <span className="icon"><RIcon name="mail" width={18} height={18} /></span>
                      <div className="info">
                        <span className="label">Email</span>
                        <strong className="val">{club.contactEmail}</strong>
                      </div>
                    </div>
                  )}
                  {club.website && (
                    <div className="detail-item-premium">
                      <span className="icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      </span>
                      <div className="info">
                        <span className="label">Sitio Web</span>
                        <strong className="val">
                          <a href={club.website.startsWith("http") ? club.website : `https://${club.website}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                            {club.website}
                          </a>
                        </strong>
                      </div>
                    </div>
                  )}
                  {club.instagram && (
                    <div className="detail-item-premium">
                      <span className="icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                        </svg>
                      </span>
                      <div className="info">
                        <span className="label">Instagram</span>
                        <strong className="val">
                          <a href={`https://instagram.com/${club.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                            {club.instagram.startsWith("@") ? club.instagram : `@${club.instagram}`}
                          </a>
                        </strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {services.length > 0 && (
              <div id="sec-servicios" className="bd-section-stacked">
                <h3 className="section-title">Servicios</h3>
                <div className="details-grid-premium">
                  {services.map((s) => (
                    <div className="detail-item-premium" key={s}>
                      <span className="icon">
                        {getServiceIcon(s)}
                      </span>
                      <div className="info">
                        <span className="label">Servicio</span>
                        <strong className="val">{SERVICE_LABELS[s] || (s === "bar" ? "Bar" : s.charAt(0).toUpperCase() + s.slice(1))}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(club.galleryimage) && club.galleryimage.length > 0 && (
              <div id="sec-fotos" className="bd-section-stacked">
                <h3 className="section-title">Galería de imágenes</h3>
                <div className="gallery-layout-premium">
                  {club.galleryimage.slice(0, 5).map((url, idx) => (
                    <div key={idx} className={`gallery-img-wrapper item-${idx}`}>
                      <img src={resolveUrl(url)} alt={`${club.name} galería ${idx + 1}`} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <ReviewsSection barId={club._id} id="sec-resenas" />
          </div>

          <div className="bd-sidebar-sticky">
              <div className="reserve-stick">
                <div className="rs-head"><div className="t">Reservá tu turno</div><div className="n">{club.name}</div></div>
                <div className="rs-body">
                  <SucursalPicker kind="canchas" currentName={club.name} sucursales={sucursales} sedes={sedes} />
                  <SucursalCurrent kind="canchas" name={club.name} casaCentral={casaCentral} />
                  <div className="field" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" }}>Fecha</label>
                    <SingleDatePickerPill value={selectedDate} onChange={setSelectedDate} minDate={minDate} maxDate={maxDate} />
                  </div>
                  {sports.length > 0 && (
                    <div className="field" style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" }}>Deporte</label>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {sports.map((s) => (
                          <button
                            type="button"
                            key={s}
                            className={`court-grid-sport-btn${selectedSport === s ? " active" : ""}`}
                            onClick={() => setSelectedSport(s)}
                            style={{ padding: "10px 18px", fontSize: "14px" }}
                          >
                            <RIcon name={getSportIcon(s)} width={16} height={16} />
                            {SPORT_LABELS[s] || s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <ClubOpenMatchesButton clubId={club._id} enabled={club.openMatches?.enabled === true} style={{ padding: 14, borderRadius: 12 }} />
                  </div>
                  <button
                    className="r-btn-primary rs-cta"
                    onClick={goToSlots}
                    disabled={!bookingsEnabled}
                    title={!bookingsEnabled ? "Este club no tiene reservas online habilitadas" : undefined}
                  >
                    Reservar Turno <RIcon name="arrowR" width={17} height={17} />
                  </button>
                </div>
              </div>

              <div className="policy-box">
                {(club.cancellationAllowed === false || club.cancellationAllowed === "false") ? (
                  <>
                    <div className="policy"><RIcon name="cal" width={16} height={16} />Las reservas se pueden realizar hasta con {club.reservationWindowDays || 6} días de antelación.</div>
                    <div className="policy"><RIcon name="alert" width={16} height={16} style={{ color: "var(--zoco-danger)" }} />Las reservas no son cancelables.</div>
                  </>
                ) : (
                  <>
                    <div className="policy"><RIcon name="cal" width={16} height={16} />Las reservas se pueden realizar hasta con {club.reservationWindowDays || 6} días de antelación.</div>
                    <div className="policy"><RIcon name="shield" width={16} height={16} />Cancelación sin cargo hasta {club.cancellationPolicyHours || 24} hs antes.</div>
                  </>
                )}
                {club.additionalNotes && (
                  <div className="policy">
                    <RIcon name="info" width={16} height={16} />
                    {club.additionalNotes}
                  </div>
                )}
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}
